import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { hrmEmployeesApi, attendanceApi, departmentsApi, designationsApi, holidaysApi, leaveTypesApi } from '@/services';

interface Pagination {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
}

interface HrmState {
  employees: any[];
  employee: any;
  attendance: any[];
  departments: any[];
  designations: any[];
  holidays: any[];
  leaveTypes: any[];
  pagination: Pagination;
  loading: boolean;
  loadingCount: number;
  error: any;
}

const initialState: HrmState = {
  employees: [],
  employee: null,
  attendance: [],
  departments: [],
  designations: [],
  holidays: [],
  leaveTypes: [],
  pagination: {
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  },
  loading: false,
  loadingCount: 0,
  error: null,
};

export const fetchEmployees = createAsyncThunk(
  'hrm/fetchEmployees',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response = await hrmEmployeesApi.getAll(params || {});
      return { data: response.data.data || [], meta: response.data.meta || {} };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchEmployee = createAsyncThunk(
  'hrm/fetchEmployee',
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response = await hrmEmployeesApi.getById(id);
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createEmployee = createAsyncThunk(
  'hrm/createEmployee',
  async (data: Record<string, any>, { rejectWithValue }) => {
    try {
      const response = await hrmEmployeesApi.create(data);
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateEmployee = createAsyncThunk(
  'hrm/updateEmployee',
  async ({ id, data }: { id: number | string, data: Record<string, any> }, { rejectWithValue }) => {
    try {
      const response = await hrmEmployeesApi.update(id, data);
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteEmployee = createAsyncThunk(
  'hrm/deleteEmployee',
  async (id: number | string, { rejectWithValue }) => {
    try {
      await hrmEmployeesApi.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDepartments = createAsyncThunk(
  'hrm/fetchDepartments',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response = await departmentsApi.getAll(params || {});
      return response.data.data || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDesignations = createAsyncThunk(
  'hrm/fetchDesignations',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response = await designationsApi.getAll(params || {});
      return response.data.data || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAttendance = createAsyncThunk(
  'hrm/fetchAttendance',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response = await attendanceApi.getAll(params || {});
      return response.data.data || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchHolidays = createAsyncThunk(
  'hrm/fetchHolidays',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response = await holidaysApi.getAll(params || {});
      return response.data.data || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchLeaveTypes = createAsyncThunk(
  'hrm/fetchLeaveTypes',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response = await leaveTypesApi.getAll(params || {});
      return response.data.data || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const hrmSlice = createSlice({
  name: 'hrm',
  initialState,
  reducers: {
    clearEmployee(state) {
      state.employee = null;
    },
    clearError(state) {
      state.error = null;
    },
    resetState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // `loading` is shared across many concurrent fetches (employees, employee,
    // departments, designations, attendance, holidays, leaveTypes, create/
    // update/delete). A plain boolean raced: the first overlapping fetch to
    // settle flipped loading off while the others were still in flight. Track
    // an in-flight counter so the flag stays true until EVERY pending request
    // settles — including refetches (a rerun while the first is pending).
    const beginFetch = (state: HrmState) => {
      state.loadingCount += 1;
      state.loading = true;
      state.error = null;
    };
    const endFetch = (state: HrmState) => {
      state.loadingCount = Math.max(0, state.loadingCount - 1);
      state.loading = state.loadingCount > 0;
    };
    const handlePending = beginFetch;
    const handleRejected = (state: HrmState, action: PayloadAction<unknown>) => {
      endFetch(state);
      state.error = action.payload;
    };

    builder
      // Fetch Employees
      .addCase(fetchEmployees.pending, handlePending)
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        endFetch(state);
        state.employees = action.payload.data;
        const meta = action.payload.meta;
        state.pagination = {
          currentPage: meta.currentPage || 1,
          lastPage: meta.totalPages || meta.lastPage || 1,
          perPage: meta.perPage || 10,
          // NOT `employees.length`: on page 2+ that is the page size, so the
          // header would claim "total {perPage}" for a larger catalogue. Keep
          // the last known total when the API omits it (`??`, not `||`, so a
          // genuine 0 stays 0).
          total: meta.total ?? state.pagination.total,
        };
      })
      .addCase(fetchEmployees.rejected, handleRejected)

      // Fetch Employee
      .addCase(fetchEmployee.pending, handlePending)
      .addCase(fetchEmployee.fulfilled, (state, action) => {
        endFetch(state);
        state.employee = action.payload;
      })
      .addCase(fetchEmployee.rejected, handleRejected)

      // Create Employee
      .addCase(createEmployee.pending, handlePending)
      .addCase(createEmployee.fulfilled, (state, action) => {
        endFetch(state);
        state.employees.unshift(action.payload);
        // Keep the header count consistent with the list: the new row was
        // prepended, so the server total has to grow with it.
        state.pagination.total += 1;
        state.pagination.lastPage = Math.max(
          state.pagination.lastPage,
          Math.ceil(state.pagination.total / Math.max(1, state.pagination.perPage)),
        );
      })
      .addCase(createEmployee.rejected, handleRejected)

      // Update Employee
      .addCase(updateEmployee.pending, handlePending)
      .addCase(updateEmployee.fulfilled, (state, action) => {
        endFetch(state);
        const index = state.employees.findIndex((e: any) => e.id === action.payload.id);
        if (index !== -1) {
          state.employees[index] = action.payload;
        }
        if (state.employee?.id === action.payload.id) {
          state.employee = action.payload;
        }
      })
      .addCase(updateEmployee.rejected, handleRejected)

      // Delete Employee
      .addCase(deleteEmployee.pending, handlePending)
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        endFetch(state);
        state.employees = state.employees.filter((e: any) => e.id !== action.payload);
        if (state.employee?.id === action.payload) {
          state.employee = null;
        }
        // The row is gone, so the counts must shrink with it — otherwise the
        // grid reports a total that includes the deleted employee.
        state.pagination.total = Math.max(0, state.pagination.total - 1);
        state.pagination.lastPage = Math.max(
          1,
          Math.ceil(state.pagination.total / Math.max(1, state.pagination.perPage)),
        );
      })
      .addCase(deleteEmployee.rejected, handleRejected)

      // Others
      .addCase(fetchDepartments.pending, handlePending)
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        endFetch(state);
        state.departments = action.payload;
      })
      .addCase(fetchDepartments.rejected, handleRejected)

      .addCase(fetchDesignations.pending, handlePending)
      .addCase(fetchDesignations.fulfilled, (state, action) => {
        endFetch(state);
        state.designations = action.payload;
      })
      .addCase(fetchDesignations.rejected, handleRejected)

      .addCase(fetchAttendance.pending, handlePending)
      .addCase(fetchAttendance.fulfilled, (state, action) => {
        endFetch(state);
        state.attendance = action.payload;
      })
      .addCase(fetchAttendance.rejected, handleRejected)

      .addCase(fetchHolidays.pending, handlePending)
      .addCase(fetchHolidays.fulfilled, (state, action) => {
        endFetch(state);
        state.holidays = action.payload;
      })
      .addCase(fetchHolidays.rejected, handleRejected)

      .addCase(fetchLeaveTypes.pending, handlePending)
      .addCase(fetchLeaveTypes.fulfilled, (state, action) => {
        endFetch(state);
        state.leaveTypes = action.payload;
      })
      .addCase(fetchLeaveTypes.rejected, handleRejected);
  },
});

export const { clearEmployee, clearError, resetState } = hrmSlice.actions;
export default hrmSlice.reducer;
